/* =============================================================================
   Store finder — Protek stockist locator (Focal 12.7.1)
   <store-finder> custom element. Self-inits via connectedCallback, tears the
   map down in disconnectedCallback (theme-editor safe). Vanilla — no theme deps.
   Map: Mapbox GL (lazy-loaded). Geocoding: postcodes.io (free, UK) + Mapbox fallback.
   ============================================================================= */
(function () {
  'use strict';

  var MAPBOX_JS  = 'https://cdn.jsdelivr.net/npm/mapbox-gl@3/dist/mapbox-gl.js';
  var MAPBOX_CSS = 'https://cdn.jsdelivr.net/npm/mapbox-gl@3/dist/mapbox-gl.css';

  var mapboxPromise = null;
  function loadMapbox() {
    if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
    if (mapboxPromise) return mapboxPromise;
    mapboxPromise = new Promise(function (resolve, reject) {
      if (!document.querySelector('link[data-mapbox-gl]')) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = MAPBOX_CSS;
        link.setAttribute('data-mapbox-gl', '');
        document.head.appendChild(link);
      }
      var s = document.createElement('script');
      s.src = MAPBOX_JS;
      s.async = true;
      s.onload = function () { window.mapboxgl ? resolve(window.mapboxgl) : reject(new Error('mapbox-gl missing')); };
      s.onerror = function () { reject(new Error('Failed to load Mapbox GL JS')); };
      document.head.appendChild(s);
    });
    return mapboxPromise;
  }

  function distanceKm(aLat, aLng, bLat, bLng) {
    var R = 6371, rad = Math.PI / 180;
    var dLat = (bLat - aLat) * rad, dLng = (bLng - aLng) * rad;
    var la1 = aLat * rad, la2 = bLat * rad;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  function fmtDistance(km) {
    var mi = km * 0.621371;
    return (mi < 10 ? mi.toFixed(1) : Math.round(mi)) + ' mi';
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i; // full postcode
  var UK_OUTCODE  = /^[A-Z]{1,2}\d[A-Z\d]?$/i;              // outward code only

  class StoreFinder extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      var d = this.dataset;
      this.token       = d.token && d.token.trim();
      this.mapStyle    = d.mapStyle || 'mapbox://styles/mapbox/streets-v12';
      this.centerLat   = parseFloat(d.centerLat) || 54.5;
      this.centerLng   = parseFloat(d.centerLng) || -3.2;
      this.zoom        = parseFloat(d.zoom) || 5;
      this.markerColor = d.markerColor || '#1f7a3d';
      this.radiusKm    = parseFloat(d.radiusKm) || 80;
      this.maxResults  = parseInt(d.maxResults, 10) || 20;

      this.mapEl     = this.querySelector('#' + d.mapId) || this.querySelector('.store-finder__map');
      this.listEl    = this.querySelector('#store-finder-results');
      this.countEl   = this.querySelector('[data-sf-count]');
      this.statusEl  = this.querySelector('[data-sf-status]');
      this.form      = this.querySelector('[data-sf-search]');
      this.input     = this.form ? this.form.querySelector('input') : null;
      this.locateBtn = this.querySelector('[data-sf-locate]');
      this.layout    = this.querySelector('.store-finder__layout');
      this.fallback  = this.querySelector('[data-sf-map-fallback]');

      try {
        this.fc = JSON.parse(this.querySelector('#store-finder-data').textContent);
      } catch (e) { this.fc = { type: 'FeatureCollection', features: [] }; }

      this.items = (this.fc.features || []).map(function (f) {
        return { id: f.properties.id, lng: f.geometry.coordinates[0], lat: f.geometry.coordinates[1], props: f.properties };
      });

      this.liById = {};
      var lis = this.listEl ? this.listEl.querySelectorAll('.store-finder__result') : [];
      Array.prototype.forEach.call(lis, function (li) { this.liById[li.dataset.id] = li; }, this);

      this._bindEvents();
      this._updateCount(this.items.length, null);
      this._collapsed = true;
      this._INITIAL_SHOWN = 15;
      this._buildShowAll();
      this._applyCollapse();
      this._setupLazyMap();
    }

    disconnectedCallback() {
      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this.map) { try { this.map.remove(); } catch (e) {} this.map = null; }
      this._init = false;
    }

    _bindEvents() {
      var self = this;
      if (this.form) {
        this.form.addEventListener('submit', function (e) { e.preventDefault(); self._search(self.input.value); });
      }
      if (this.locateBtn) {
        this.locateBtn.addEventListener('click', function () { self._useMyLocation(); });
      }
      this.addEventListener('click', function (e) {
        var btn = e.target.closest('.store-finder__toggle-btn');
        if (btn) { self._setView(btn.dataset.sfView); }
      });
      if (this.listEl) {
        this.listEl.addEventListener('click', function (e) {
          if (e.target.closest('a')) return; // let phone/directions links work
          var li = e.target.closest('.store-finder__result');
          if (li) self._focusStore(li.dataset.id);
        });
      }
    }

    _setView(view) {
      if (this.layout) this.layout.setAttribute('data-sf-view', view);
      var btns = this.querySelectorAll('.store-finder__toggle-btn');
      Array.prototype.forEach.call(btns, function (b) {
        var active = b.dataset.sfView === view;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (view === 'map' && this.map) this.map.resize();
    }

    _status(msg, state) {
      if (!this.statusEl) return;
      this.statusEl.textContent = msg || '';
      if (state) this.statusEl.setAttribute('data-state', state);
      else this.statusEl.removeAttribute('data-state');
    }

    _updateCount(n, label) {
      if (!this.countEl) return;
      var noun = (n === 1 ? 'stockist' : 'stockists');
      this.countEl.textContent = label ? (n + ' ' + noun + ' near ' + label) : (n + ' ' + noun);
    }

    // Collapse the un-searched list to a manageable length with a Show all toggle,
    // so the page isn't the full height of every stockist on first load.
    _buildShowAll() {
      if (!this.listEl || this.items.length <= this._INITIAL_SHOWN) return;
      var self = this;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'button button--outline store-finder__show-all';
      btn.addEventListener('click', function () { self._collapsed = !self._collapsed; self._applyCollapse(); });
      this.listEl.insertAdjacentElement('afterend', btn);
      this._showAllBtn = btn;
    }

    _applyCollapse() {
      if (!this.listEl) return;
      var self = this;
      Array.prototype.forEach.call(this.listEl.querySelectorAll('.store-finder__result'), function (li, i) {
        li.hidden = self._collapsed && i >= self._INITIAL_SHOWN;
      });
      if (this._showAllBtn) {
        this._showAllBtn.hidden = false;
        this._showAllBtn.textContent = this._collapsed
          ? ('Show all ' + this.items.length + ' stockists')
          : 'Show fewer';
      }
    }

    /* ---- map ---------------------------------------------------------------- */
    _setupLazyMap() {
      var self = this;
      if (!this.token) { this._showFallback('Add your Mapbox public token in the theme editor to display the map. The stockist list below works without it.'); return; }
      if (!('IntersectionObserver' in window)) { this._initMap(); return; }
      this._io = new IntersectionObserver(function (entries) {
        if (entries.some(function (en) { return en.isIntersecting; })) {
          self._io.disconnect(); self._io = null; self._initMap();
        }
      }, { rootMargin: '300px' });
      this._io.observe(this.mapEl);
    }

    _showFallback(msg) {
      if (this.fallback) { this.fallback.textContent = msg; this.fallback.hidden = false; }
      if (this.mapEl) this.mapEl.style.display = 'none';
    }

    _ensureMapInit() {
      // Safety net: guarantee the map loads on interaction, even if the
      // lazy IntersectionObserver never fires (rare, but cheap to guard).
      if (!this.token || this._mapStarted) return;
      if (this._io) { this._io.disconnect(); this._io = null; }
      this._initMap();
    }

    _initMap() {
      if (this._mapStarted) return;
      this._mapStarted = true;
      var self = this;
      loadMapbox().then(function (mapboxgl) {
        self.mapboxgl = mapboxgl;
        mapboxgl.accessToken = self.token;
        self.map = new mapboxgl.Map({
          container: self.mapEl,
          style: self.mapStyle,
          center: [self.centerLng, self.centerLat],
          zoom: self.zoom,
          cooperativeGestures: true
        });
        self.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
        self.map.on('load', function () { self._addStockistLayers(); });
        self.map.on('error', function () {/* keep silent; tile errors shouldn't break UI */});
      }).catch(function () {
        self._showFallback('The map could not be loaded — please check the Mapbox token. The stockist list still works.');
        self._status('Map unavailable — the list still works.', 'error');
      });
    }

    _addStockistLayers() {
      var self = this, map = this.map;
      map.addSource('stockists', { type: 'geojson', data: this.fc, cluster: true, clusterMaxZoom: 11, clusterRadius: 45 });

      map.addLayer({
        id: 'clusters', type: 'circle', source: 'stockists', filter: ['has', 'point_count'],
        paint: { 'circle-color': this.markerColor, 'circle-opacity': 0.85,
                 'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 50, 26] }
      });
      map.addLayer({
        id: 'cluster-count', type: 'symbol', source: 'stockists', filter: ['has', 'point_count'],
        layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-size': 12,
                  'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'] },
        paint: { 'text-color': '#ffffff' }
      });
      map.addLayer({
        id: 'unclustered', type: 'circle', source: 'stockists', filter: ['!', ['has', 'point_count']],
        paint: { 'circle-color': this.markerColor, 'circle-radius': 7, 'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff' }
      });

      map.on('click', 'clusters', function (e) {
        var f = map.queryRenderedFeatures(e.point, { layers: ['clusters'] })[0];
        if (!f) return;
        map.getSource('stockists').getClusterExpansionZoom(f.properties.cluster_id, function (err, zoom) {
          if (err) return;
          map.easeTo({ center: f.geometry.coordinates, zoom: zoom });
        });
      });
      map.on('click', 'unclustered', function (e) {
        var f = e.features[0];
        self._openPopup(f.geometry.coordinates.slice(), f.properties);
        self._highlightList(f.properties.id);
      });
      ['clusters', 'unclustered'].forEach(function (id) {
        map.on('mouseenter', id, function () { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', id, function () { map.getCanvas().style.cursor = ''; });
      });

      if (this._pendingFocus) {
        this._applyResultsToMap(this._pendingFocus);
        this._pendingFocus = null;
      } else if (!this._origin && this.items.length) {
        // first load, no search yet → frame the whole stockist network so the
        // south of the UK isn't pushed off the bottom of the map
        var b = new this.mapboxgl.LngLatBounds();
        this.items.forEach(function (it) { b.extend([it.lng, it.lat]); });
        map.fitBounds(b, { padding: 48, maxZoom: 9, duration: 0 });
      }
    }

    _openPopup(lngLat, p) {
      if (!this.map || !this.mapboxgl) return;
      var phone = p.phone ? String(p.phone).replace(/[^0-9+]/g, '') : '';
      var html =
        '<div class="store-finder__popup">' +
          '<h3 class="store-finder__popup-name heading h6">' + esc(p.name) + '</h3>' +
          '<p class="store-finder__popup-address">' + esc(p.address) + '</p>' +
          '<div class="store-finder__popup-actions">' +
            (phone ? '<a class="store-finder__result-phone" href="tel:' + esc(phone) + '">' + esc(p.phone) + '</a>' : '') +
            '<a class="button button--outline button--small" target="_blank" rel="noopener nofollow" href="' + esc(p.directions) + '">Directions</a>' +
          '</div>' +
        '</div>';
      if (this._popup) this._popup.remove();
      this._popup = new this.mapboxgl.Popup({ offset: 14, maxWidth: '280px' })
        .setLngLat(lngLat).setHTML(html).addTo(this.map);
    }

    /* ---- search / geocoding ------------------------------------------------- */
    _useMyLocation() {
      var self = this;
      if (!navigator.geolocation) { this._status('Location isn’t supported by your browser.', 'error'); return; }
      this._status('Locating you…');
      navigator.geolocation.getCurrentPosition(
        function (pos) { self._showResults(pos.coords.latitude, pos.coords.longitude, 'your location'); },
        function (err) {
          self._status(err.code === 1
            ? 'Location permission was denied — try searching by postcode instead.'
            : 'Couldn’t get your location — try searching by postcode instead.', 'error');
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
      );
    }

    _search(raw) {
      var self = this;
      var q = (raw || '').trim();
      if (!q) { this._status('Enter a postcode or town to search.'); if (this.input) this.input.focus(); return; }
      this._status('Searching…');
      this._geocode(q).then(function (loc) {
        if (!loc) { self._status('We couldn’t find “' + q + '”. Check the spelling, or try a postcode.', 'error'); return; }
        self._showResults(loc.lat, loc.lng, loc.label || q);
      }).catch(function () { self._status('Something went wrong searching — please try again.', 'error'); });
    }

    _geocode(q) {
      var self = this;
      var clean = q.replace(/\s+/g, ' ').trim();
      if (UK_POSTCODE.test(clean)) {
        return fetch('https://api.postcodes.io/postcodes/' + encodeURIComponent(clean.replace(/\s+/g, '')))
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (j) {
            if (j && j.result) return { lat: j.result.latitude, lng: j.result.longitude, label: j.result.postcode };
            return self._geocodePartialPostcode(clean);
          });
      }
      if (UK_OUTCODE.test(clean)) {
        return fetch('https://api.postcodes.io/outcodes/' + encodeURIComponent(clean))
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (j) {
            if (j && j.result) return { lat: j.result.latitude, lng: j.result.longitude, label: clean.toUpperCase() };
            return self._geocodePlace(clean);
          });
      }
      return self._geocodePlace(clean);
    }

    _geocodePartialPostcode(pc) {
      var self = this;
      return fetch('https://api.postcodes.io/postcodes?q=' + encodeURIComponent(pc) + '&limit=1')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          if (j && j.result && j.result[0]) return { lat: j.result[0].latitude, lng: j.result[0].longitude, label: j.result[0].postcode };
          return self._geocodeMapbox(pc);
        });
    }

    _geocodePlace(name) {
      var self = this;
      return fetch('https://api.postcodes.io/places?q=' + encodeURIComponent(name) + '&limit=1')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          if (j && j.result && j.result[0]) return { lat: j.result[0].latitude, lng: j.result[0].longitude, label: j.result[0].name_1 || name };
          return self._geocodeMapbox(name);
        });
    }

    _geocodeMapbox(q) {
      if (!this.token) return Promise.resolve(null);
      var url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' + encodeURIComponent(q) +
        '.json?country=gb&limit=1&types=postcode,place,locality,address,neighborhood&access_token=' + encodeURIComponent(this.token);
      return fetch(url).then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          if (j && j.features && j.features[0]) return { lat: j.features[0].center[1], lng: j.features[0].center[0], label: j.features[0].text || q };
          return null;
        });
    }

    /* ---- results ------------------------------------------------------------ */
    _showResults(lat, lng, label) {
      var self = this;
      this._ensureMapInit();
      var ranked = this.items.map(function (it) { return { it: it, d: distanceKm(lat, lng, it.lat, it.lng) }; })
        .sort(function (a, b) { return a.d - b.d; });

      var within = ranked.filter(function (x) { return x.d <= self.radiusKm; }).slice(0, this.maxResults);
      var shown = within.length ? within : ranked.slice(0, 5);

      this._renderList(shown, label, within.length === 0);
      this._origin = { lat: lat, lng: lng, label: label };

      if (this.map) this._applyResultsToMap({ origin: this._origin, shown: shown });
      else this._pendingFocus = { origin: this._origin, shown: shown };

      this._setView('list');
    }

    _renderList(shown, label, noneWithin) {
      var self = this;
      if (this._showAllBtn) this._showAllBtn.hidden = true;   // search results define the list
      this.items.forEach(function (it) { var li = self.liById[it.id]; if (li) li.hidden = true; });
      shown.forEach(function (x) {
        var li = self.liById[x.it.id];
        if (!li) return;
        li.hidden = false;
        var nameEl = li.querySelector('.store-finder__result-name');
        if (nameEl) {
          var dist = nameEl.querySelector('.store-finder__result-distance');
          if (!dist) {
            dist = document.createElement('span');
            dist.className = 'store-finder__result-distance';
            nameEl.appendChild(document.createTextNode(' '));
            nameEl.appendChild(dist);
          }
          dist.textContent = '· ' + fmtDistance(x.d);
        }
        self.listEl.appendChild(li); // reorder by distance
      });
      this._updateCount(shown.length, label);
      this._status(noneWithin
        ? ('No stockists within ' + Math.round(this.radiusKm) + ' km of ' + label + ' — showing the nearest few.')
        : ('Showing the ' + shown.length + ' nearest to ' + label + '.'));
    }

    _applyResultsToMap(data) {
      var map = this.map, mapboxgl = this.mapboxgl;
      if (this._originMarker) this._originMarker.remove();
      var el = document.createElement('div');
      el.style.cssText = 'width:16px;height:16px;border-radius:50%;background:#1a73e8;border:3px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.25)';
      this._originMarker = new mapboxgl.Marker({ element: el }).setLngLat([data.origin.lng, data.origin.lat]).addTo(map);

      var bounds = new mapboxgl.LngLatBounds();
      bounds.extend([data.origin.lng, data.origin.lat]);
      data.shown.forEach(function (x) { bounds.extend([x.it.lng, x.it.lat]); });
      map.fitBounds(bounds, { padding: 60, maxZoom: 13, duration: 700 });

      if (data.shown[0]) {
        var n = data.shown[0].it;
        this._openPopup([n.lng, n.lat], n.props);
        this._highlightList(n.id);
      }
    }

    _focusStore(id) {
      var it = this.items.filter(function (x) { return String(x.id) === String(id); })[0];
      if (!it) return;
      this._highlightList(id);
      if (this.map) {
        this.map.flyTo({ center: [it.lng, it.lat], zoom: Math.max(this.map.getZoom(), 12), duration: 600 });
        this._openPopup([it.lng, it.lat], it.props);
        if (window.matchMedia('(max-width: 740px)').matches) this._setView('map');
      }
    }

    _highlightList(id) {
      var self = this;
      Object.keys(this.liById).forEach(function (k) { self.liById[k].classList.toggle('is-active', String(k) === String(id)); });
      var li = this.liById[id];
      if (li && !li.hidden && li.scrollIntoView) li.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  if (!customElements.get('store-finder')) customElements.define('store-finder', StoreFinder);
})();
